import { Alert, Badge, Flex, List, Loader as MantineLoader, Progress, Table, Text, ThemeIcon } from '@mantine/core'
import { IconAlertTriangle, IconCheck, IconSend, IconX } from '@tabler/icons-react'
import HelpTooltip from 'common/HelpTooltip'
import Loader from 'common/Loader'
import ShibaButton from 'common/ShibaButton'
import { getErrorMessage } from 'errors'
import { useFetchTrackingNumbers, useGenerateShipments, useOrdersForShipment } from 'hooks/orders'
import { useEffect, useMemo, useState } from 'react'
import { DeliveryCompanyName, Order } from 'types/orders'

type Props = {
  deliveryCompanyName: DeliveryCompanyName
  selectedOrderIds: Array<number>
}

export default function DispatchParcelsTable({ deliveryCompanyName, selectedOrderIds }: Props) {
  const randomKey = useMemo(() => Math.random().toString(36).substring(2, 15), [])
  const ordersForShipment = useOrdersForShipment(deliveryCompanyName, selectedOrderIds)
  const { data, isPending, isSuccess, isError, error, mutate } = useGenerateShipments(deliveryCompanyName)
  const [orderIDsWithoutTracking, setOrderIDsWithoutTracking] = useState<Array<number>>([])
  const [totalShipmentsCount, setTotalShipmentsCount] = useState(0)
  const fetchTrackingNumbers = useFetchTrackingNumbers(randomKey, orderIDsWithoutTracking)
  const [initialAlreadyDispatched, setInitialAlreadyDispatched] = useState<Array<Order> | null>(null)

  useEffect(() => {
    if (ordersForShipment.isSuccess && initialAlreadyDispatched === null) {
      setInitialAlreadyDispatched(ordersForShipment.data.alreadyDispatched)
    }
  }, [ordersForShipment.isSuccess, ordersForShipment.data])

  useEffect(() => {
    // If there are shipments without tracking numbers, update the state to trigger fetching tracking numbers
    if (data) {
      const withoutTracking = data.filter((s) => !s.trackingNumber && !s.errorMessage).map((s) => s.orderId)
      setOrderIDsWithoutTracking(withoutTracking)
      setTotalShipmentsCount(data.length)
    }
  }, [data])

  useEffect(() => {
    // Remove order IDs that have received a tracking number or an error message
    if (fetchTrackingNumbers.data) {
      const resolved = new Set(
        fetchTrackingNumbers.data.filter((s) => s.trackingNumber || s.errorMessage).map((s) => s.orderId)
      )
      if (resolved.size > 0) {
        setOrderIDsWithoutTracking((prev) => prev.filter((id) => !resolved.has(id)))
      }
    }
  }, [fetchTrackingNumbers.data])

  if (ordersForShipment.isLoading) {
    return <Loader />
  } else if (ordersForShipment.isError) {
    return <div>Wystąpił błąd podczas ładowania danych.</div>
  } else if (ordersForShipment.isSuccess) {
    const { dispatchable } = ordersForShipment.data
    const alreadyDispatched = initialAlreadyDispatched ?? []
    const failedShipmentsFromMutation = data?.filter((s) => !!s.errorMessage)
    const successfulShipmentsFromMutation = data?.filter((s) => !s.errorMessage)
    const failedShipmentsFromQuery = fetchTrackingNumbers.data?.filter((s) => !!s.errorMessage)
    const failedShipments = [...(failedShipmentsFromMutation || []), ...(failedShipmentsFromQuery || [])]
    const shipmentsCount = dispatchable.length
    const successfullyShippedCount = successfulShipmentsFromMutation?.length || 0

    return (
      <>
        {alreadyDispatched.length > 0 && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            color="yellow"
            title="Część zamówień została już nadana"
            mb="sm"
            mt="sm"
          >
            <Text size="sm" mb={4}>
              Następujące zamówienia zostały pominięte, ponieważ przesyłka została już nadana:
            </Text>
            <List size="sm">
              {alreadyDispatched.map((o) => (
                <List.Item key={o.id}>
                  Z-{o.id} — {o.firstName} {o.lastName}
                </List.Item>
              ))}
            </List>
          </Alert>
        )}
        <Table variant="vertical">
          <Table.Tbody>
            <Table.Tr>
              <Table.Th w="25%">Kurier</Table.Th>
              <Table.Td>{deliveryCompanyName}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Th>Status</Table.Th>
              <Table.Td>
                <Flex gap="sm" align="center">
                  {isPending ? (
                    <>
                      <MantineLoader size={16} color="blue" />
                      <Text size="sm">Nadawanie przesyłek w toku...</Text>
                    </>
                  ) : orderIDsWithoutTracking.length > 0 || fetchTrackingNumbers.isFetching ? (
                    <>
                      <MantineLoader size={16} color="blue" />
                      <Text size="sm">
                        Przesyłki nadane. Pobieranie danych do śledzenia. Może to potrwać kilka minut. Nie zamykaj tego
                        okna. Pozostało zamówień: {orderIDsWithoutTracking.length}.
                      </Text>
                    </>
                  ) : (
                    <>
                      {!isSuccess && !isError && (
                        <>
                          {shipmentsCount > 0 ? (
                            <>
                              <Badge color="blue" size="xs" circle />
                              <Text size="sm">
                                {shipmentsCount}{' '}
                                {shipmentsCount === 1
                                  ? 'przesyłka'
                                  : shipmentsCount >= 2 && shipmentsCount <= 4
                                    ? 'przesyłki'
                                    : 'przesyłek'}{' '}
                                do nadania
                              </Text>
                              <HelpTooltip
                                text={`Zamówienia: ${dispatchable.map((order) => `Z-${order.id}`).join(', ')}`}
                              />
                            </>
                          ) : (
                            <>
                              <ThemeIcon color="green" radius="xl" size="xs">
                                <IconCheck />
                              </ThemeIcon>
                              <Text size="sm">Brak przesyłek do nadania</Text>
                            </>
                          )}
                        </>
                      )}
                      {(isSuccess || fetchTrackingNumbers.isSuccess) &&
                        (failedShipments?.length ? (
                          <>
                            {successfullyShippedCount > 0 && (
                              <>
                                <ThemeIcon color="green" radius="xl" size="xs">
                                  <IconCheck />
                                </ThemeIcon>
                                <Text size="sm">
                                  Nadano {successfullyShippedCount}{' '}
                                  {successfullyShippedCount === 1
                                    ? 'przesyłkę'
                                    : successfullyShippedCount >= 2 && successfullyShippedCount <= 4
                                      ? 'przesyłki'
                                      : 'przesyłek'}
                                </Text>
                              </>
                            )}
                            <ThemeIcon color="red" radius="xl" size="xs">
                              <IconX />
                            </ThemeIcon>
                            <Text size="sm">
                              Nie udało się nadać {failedShipments.length}{' '}
                              {failedShipments.length === 1 ? 'przesyłki' : 'przesyłek'}
                            </Text>
                          </>
                        ) : (
                          <>
                            <ThemeIcon color="green" radius="xl" size="xs">
                              <IconCheck />
                            </ThemeIcon>
                            <Text size="sm">Przesyłki nadane!</Text>
                          </>
                        ))}
                      {(isError || fetchTrackingNumbers.isError) && (
                        <>
                          <ThemeIcon color="red" radius="xl" size="xs">
                            <IconX />
                          </ThemeIcon>
                          <Text size="sm">Błąd podczas nadawania przesyłek</Text>
                        </>
                      )}
                    </>
                  )}
                </Flex>
              </Table.Td>
            </Table.Tr>
            {(orderIDsWithoutTracking.length > 0 || fetchTrackingNumbers.isFetching) && (
              <Table.Tr>
                <Table.Th>Postęp</Table.Th>
                <Table.Td>
                  <Progress
                    radius="md"
                    size="lg"
                    value={
                      totalShipmentsCount > 0
                        ? ((totalShipmentsCount - orderIDsWithoutTracking.length) / totalShipmentsCount) * 100
                        : 0
                    }
                    animated
                  />
                </Table.Td>
              </Table.Tr>
            )}
            <Table.Tr>
              <Table.Th>Akcja</Table.Th>
              <Table.Td>
                <ShibaButton
                  variant="light"
                  size="xs"
                  leftSection={<IconSend size="18" />}
                  onClick={() => mutate({ orderIds: dispatchable.map((order) => order.id) })}
                  tooltipPosition="bottom"
                  disabledReason={
                    shipmentsCount === 0
                      ? 'Brak przesyłek do nadania'
                      : isPending || fetchTrackingNumbers.isEnabled
                        ? 'Nadawanie w toku'
                        : undefined
                  }
                >
                  Nadaj {isError || (failedShipments && failedShipments.length > 0) ? 'ponownie' : ''}
                </ShibaButton>
              </Table.Td>
            </Table.Tr>
            {error && (
              <Table.Tr>
                <Table.Th>Błędy</Table.Th>
                <Table.Td>{getErrorMessage(error)}</Table.Td>
              </Table.Tr>
            )}
            {fetchTrackingNumbers.isError && (
              <Table.Tr>
                <Table.Th>Błędy</Table.Th>
                <Table.Td>{getErrorMessage(fetchTrackingNumbers.error)}</Table.Td>
              </Table.Tr>
            )}
            {failedShipments && failedShipments.length > 0 && (
              <Table.Tr>
                <Table.Th>Błędy</Table.Th>
                <Table.Td>
                  <List size="sm">
                    {failedShipments.map((s) => (
                      <List.Item key={s.orderId}>
                        <b>Z-{s.orderId}:</b> {s.errorMessage}
                      </List.Item>
                    ))}
                  </List>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </>
    )
  }
}
